import { useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { OrgContext } from '../org/OrgContext';
import { workspacePath } from '../lib/workspacePath';

/**
 * Build workspace-scoped paths. Every workspace URL is `/w/<slug>/…`, so links
 * inside the workspace prepend the current slug. The slug comes from the route
 * (`/w/:slug`) when present, falling back to the active workspace — so the
 * helper also works from the nav shell, which renders above the `:slug` route.
 *
 * `wp('/projects/RV')` -> `/w/acme/projects/RV`; `wp('/')` -> `/w/acme`.
 *
 * Reads OrgContext directly (not via useOrg) and tolerates its absence: with no
 * slug in scope, `wp()` returns the path unchanged. That keeps component unit
 * tests — which render a page without a router slug or OrgProvider — asserting
 * the same bare paths as before, while the real app always has a slug.
 */
export function useWorkspacePath(): {
  slug: string;
  wp: (path: string) => string;
} {
  const { slug: routeSlug } = useParams();
  const ctx = useContext(OrgContext);
  const slug = routeSlug ?? ctx?.activeOrg?.slug ?? '';

  const wp = useCallback((path: string) => workspacePath(slug, path), [slug]);

  return { slug, wp };
}
